import React, { useState, useEffect } from 'react';
import Home from './pages/Home.js';
import Profile from './pages/Profile.js';
import { CircularProgress } from '@material-ui/core';
import Login from './pages/Login.js';
import TeacherLogin from './pages/TeacherLogin.jsx';
import Questions from './pages/Questions';
import ResultDetails from './pages/ResultDetails';
import Results from './pages/Results';
import SurveyLanding from './pages/SurveyLanding';
import SurveyComplete from './pages/SurveyComplete';
import ResultExtended from './pages/ResultExtended';
import ErrorPage from './pages/Error';
import NotFound from './pages/404';
import { isLoggedIn } from './utils/isLoggedIn';
import About from './pages/About.js';
import Contact from './pages/Contact.js';
import Faq from './pages/Faq.js';
import AdminDash from './pages/AdminDashboard.js';
import StudentDashboard from './pages/StudentDashboard.js';
import InstructorDashboard from './pages/InstructorDashboard.js';
import QutSignup from './pages/QutSignup.js';
import InvitePage from "./pages/InvitePage";
import PrivacyPolicy from './pages/PrivacyPolicy.js';

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import AppContext from './AppContext.js';
import Stripe from './pages/Stripe.js';
import { getIsProUser } from './utils/isProUser.js';
import axios from "axios";
import WorkspaceLayout from './components/WorkspaceLayout';

axios.defaults.baseURL = "https://entremap.herokuapp.com";
axios.defaults.withCredentials = true;

// ----------------- Global fetch interceptor -----------------
const ignoreUrl = [
  'api/user/icon',
  'api/auth/verify',
  'api/user/getUsers',
  'api/auth',
  'api/cohort'
];
const errStatus = [400, 401, 403, 404];

try {
  const _fetch = window.fetch;
  window.fetch = (input, init = {}) => {
    const method = (init && init.method ? init.method : 'GET').toUpperCase();
    return _fetch(input, init).then((res) => {
      if (res) {
        const requestedUrl =
            res.url ||
            (typeof input === 'string' ? input : (input && input.url) || '');

        const isAuthApi = requestedUrl.includes('/api/auth/');
        const shouldIgnore =
            isAuthApi || ignoreUrl.some((it) => requestedUrl.includes(it));

        if (errStatus.includes(res.status) && !shouldIgnore && method === 'GET') {
          window.location.href = `/error?errCode=${res.status}`;
        }
      }
      return res;
    });
  };
} catch (e) {
  // noop
}
// ------------------------------------------------------------

function App() {
  const [status, setStatus] = useState('loading');
  const [isProUser, setIsProUser] = useState(false);

  const requestIsProUser = () => {
    return getIsProUser().then((_isProUser) => {
      setIsProUser(_isProUser);
      return _isProUser;
    });
  };

  useEffect(() => {
    isLoggedIn()
        .then((success) => {
          setStatus(success ? true : false);
          return success;
        })
        .then((success) => {
          if (success) {
            requestIsProUser();
          }
        });
  }, []);

  // Client-side route guard — UX affordance only, not a security boundary. Server-side middleware enforces access control.
  const PrivateRoute = ({ component: Component, ...rest }) => {
    return (
        <Route


            {...rest}
            render={(props) => {
              if (status === 'loading') {
                return (
                    <CircularProgress
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                    />
                );
              } else if (status === true) {
                return <Component {...props} />;
              } else {
                // Preserve the intended URL including query params
                const redirectUrl = props.location.pathname + props.location.search;
                return <Redirect to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} />;
              }
            }}
        />
    );
  };

  const AppRoute = ({ component: Component, ...rest }) => {
    return (
        <Route
            {...rest}
            render={(props) => {
              if (status === 'loading') {
                return (
                    <CircularProgress
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                    />
                );
              } else {
                return <Component {...props} isLoggedIn={status} />;
              }
            }}
        />
    );
  };

  return (
      <AppContext.Provider
          value={{ isLoggedIn: status, isProUser, requestIsProUser: getIsProUser }}
      >
        <Router>
          <WorkspaceLayout isLoggedIn={status}>
          <Switch>
            <AppRoute exact path="/" component={Home} />

            <Route
                path="/login"
                render={(props) => {
                  if (status === 'loading') {
                    return (
                        <CircularProgress
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                        />
                    );
                  } else if (status === true) {
                    // Already logged in - check if there's a redirect URL
                    const params = new URLSearchParams(props.location.search);
                    const redirectUrl = params.get('redirect');
                    if (redirectUrl) {
                      return <Redirect to={redirectUrl} />;
                    }
                    return <Redirect to="/profile" />;
                  } else {
                    return <Login {...props} />;
                  }
                }}
            />

            <Route
                path="/teacher-login"
                render={(props) => {
                  if (status === 'loading') {
                    return (
                        <CircularProgress
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                        />
                    );
                  } else if (status === true) {
                    const params = new URLSearchParams(props.location.search);
                    const redirectUrl = params.get('redirect');
                    if (redirectUrl) {
                      return <Redirect to={redirectUrl} />;
                    }
                    return <Redirect to="/instructor" />;
                  } else {
                    return <TeacherLogin {...props} />;
                  }
                }}
            />

            <AppRoute path="/qut-signup" component={QutSignup} />
            <AppRoute path="/questions" component={Questions} />
            <AppRoute path="/results" component={Results} />
            <AppRoute path="/resultdetails" component={ResultDetails} />
            <AppRoute path="/surveyComplete" component={SurveyComplete} />
            <PrivateRoute path="/profile" component={Profile} />
            <AppRoute path="/surveylanding" component={SurveyLanding} />
            <AppRoute path="/resultExtended" component={ResultExtended} />
            <AppRoute path="/about" component={About} />
            <AppRoute path="/contact" component={Contact} />
            <AppRoute path="/stripe" component={Stripe} />
            <AppRoute path="/error" component={ErrorPage} />
            <AppRoute path="/PrivacyPolicy" component={PrivacyPolicy} />
            <AppRoute path="/faq" component={Faq} />
            <AppRoute path="/adminDashboard" component={AdminDash} />
            <AppRoute path="/StudentDashboard" component={StudentDashboard} />
            <AppRoute path="/instructor" component={InstructorDashboard} />
            <AppRoute path="/invite" component={InvitePage} />
            <Route component={NotFound} />
          </Switch>
          </WorkspaceLayout>
        </Router>
      </AppContext.Provider>
  );
}


export default App;
